
import { defineComponent, reactive, ref, computed, watch } from 'vue'
import { ElForm } from 'element-plus'
import { AxiosResponse } from 'axios'
import { useStore, Store } from '@/store'

import JrBoundaryStep from '@/components/jr-parts/JrBoundaryStep.vue'
import JrRegistConfirmMessage from '@/components/jr-parts/JrRegistConfirmMessage.vue'
import JrGeocoder from '@/components/jr-parts/JrGeocoder.vue'
import JrMap from '@/components/jr-parts/JrMap.vue'
import JrCheckbox from '@/components/jr-parts/JrCheckbox.vue'
import JrNumber from '@/components/jr-parts/JrNumber.vue'
import JrText from '@/components/jr-parts/JrText.vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'
import InputGroup from '@/components/layout/InputGroup.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import FormItemDisabled from '@/components/layout/FormItemDisabled.vue'
import LineSelect from '@/components/inputs/LineSelect.vue'
import StationSelect from '@/components/inputs/StationSelect.vue'
import PersonalInfo from '@/components/displays/PersonalInfo.vue'
import TmpBoundaryLogin from '@/components/auth-wrappers/TmpBoundaryLogin.vue'
import RequestReasonTypeSelect from '@/components/inputs/RequestReasonTypeSelect.vue'
import BoundaryNext from '@/components/actions/BoundaryNext.vue'
import BoundaryPrev from '@/components/actions/BoundaryPrev.vue'
import JrInputView from '@/components/jr-parts/JrInputView.vue'

import { DEFAULT_CENTER_LAT, DEFAULT_CENTER_LNG, DEFAULT_ZOOM, PIN_ZOOM } from '@/constants/map'
import { Geocoder } from '@/services/googleMap'
import { addMessageQueue, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import MessageService from '@/services/message'
import { boundaryRule } from '@/constants/rules'
import { BoundarySteps } from '@/constants/enums'
import { BoundaryForm } from '@/types/forms'
import { FileTypes, RequestReasonTypes, APPLICANT_UPLOAD_FILE_EXTS } from '@/constants/enums'
import { TmpBoundaryGet } from '@/api'
import { ApplicantReadRequest } from '@/requests/tmpBoundary'

/**
 * 画面をスクロールする
 */
const scrollTop = () => {
  window.scrollTo({
    top: 0,
  })
}
/**
 * 境界立会申請 本登録画面
 */
export default defineComponent({
  name: 'Boundary',
  components: {
    JrRegistConfirmMessage,
    InputGroup,
    InputLabel,
    JrText,
    JrCheckbox,
    LineSelect,
    StationSelect,
    FormItemDisabled,
    JrFileInput,
    PersonalInfo,
    TmpBoundaryLogin,
    RequestReasonTypeSelect,
    JrBoundaryStep,
    JrNumber,
    JrGeocoder,
    JrMap,
    BoundaryNext,
    BoundaryPrev,
    JrInputView,
  },
  setup() {
    const step = ref<number>(BoundarySteps.INPUT)
    // 表示している仮申請ID
    const viewTmpBoundaryId = ref<number | null>(null)
    const applicantNumber = ref<string | null>(null)
    const store = useStore() as Store

    const mapCenterLat = ref<string>(DEFAULT_CENTER_LAT)
    const mapCenterLng = ref<string>(DEFAULT_CENTER_LNG)
    const mapZoom = ref<number>(DEFAULT_ZOOM)
    const changeMapCenter = () => {
      mapZoom.value = PIN_ZOOM
      // 中心座標変更時は工事場所住所をリセットする
      form.latitude = ''
      form.longitude = ''
    }
    const form = reactive<BoundaryForm>({
      request_header: {
        applicant_email: '',
        applicant_manager_name: '',
        applicant_address: '',
        applicant_tel: '',
      },
      agent_address: '',
      agent_corporate_name: '',
      agent_name: '',
      contact_tel: '',

      request_reason_type: -1,
      request_reason_type_other: '',
      is_boundary_marker_confirm: false,
      is_boundary_marker_restore: false,
      is_boundary_fixed_surveying: false,

      location: '',
      parcel_number: '',
      land_category: '',
      parcel_area: '',
      line: null,
      line_name: '',
      start_station: null,
      end_station: null,
      start_station_name: '',
      end_station_name: '',
      latitude: '',
      longitude: '',

      jr_site_location: '',
      jr_site_parcel_number: '',
      boundary_application_form_file: -1,
      identification_file: -1,
      power_of_attorney_file: -1,
      location_map_file: -1,
      ground_plan_file: -1,
      survey_drawing_of_acreage_file: -1,
      cadastral_map_file: -1,
      certificate_of_registered_matters_file: -1,
      actual_place_photo_file: -1,
      personal_agree: false,
      boundary_application_form_file_selected: false,
      identification_file_selected: false,
      power_of_attorney_file_selected: false,
      location_map_file_selected: false,
      ground_plan_file_selected: false,
      survey_drawing_of_acreage_file_selected: false,
      cadastral_map_file_selected: false,
      certificate_of_registered_matters_file_selected: false,
      actual_place_photo_file_selected: false,
      work_content_count: 0,
    })

    const validationError = ref<{ start_station?: string; end_station?: string }>({})

    // 仮申請IDが取れ次第、情報を取得する
    watch(
      () => store.getters['tmpBoundary/getId'],
      async (tmpBoundaryId: number | null) => {
        if (tmpBoundaryId == null || viewTmpBoundaryId.value != null) {
          // id無し(未ログイン)か既に取得済みの場合は何もしない
          return
        }
        const request = new ApplicantReadRequest(tmpBoundaryId)
        await request.exec()
        if (request.success) {
          const { data } = request.getResponse() as AxiosResponse<TmpBoundaryGet>

          // 取得した情報をformに入れる
          Object.assign(form, data)
          // メールアドレスを移し替え
          form.request_header.applicant_email = data.applicant_email
          viewTmpBoundaryId.value = tmpBoundaryId
          // 所在からmapの初期位置を検索
          const geocoder = new Geocoder()
          const result = await geocoder.search(form.location)
          if (result === null) {
            addMessageQueue(MESSAGE_TYPE_ERROR, MessageService.codes.GEOCODER_ADDRESS_NOT_FOUND)
          } else {
            mapZoom.value = PIN_ZOOM
            mapCenterLat.value = result.lat.toPrecision(9)
            mapCenterLng.value = result.lng.toPrecision(9)
          }
        }
      },
      { immediate: true }
    )

    // 開始駅・終了駅の監視
    watch(
      () => [form.start_station, form.end_station],
      () => {
        if ((form.start_station == null) === (form.end_station == null)) {
          // どちらも入力されたか、どちらも入力されていない場合エラーをクリアする
          validationError.value = {}
        }
      },
      { deep: true }
    )
    // 作業内容選択数の監視
    watch(
      () => [form.is_boundary_marker_confirm, form.is_boundary_marker_restore, form.is_boundary_fixed_surveying],
      (workContents) => {
        // trueの合計を調べる
        form.work_content_count = workContents.filter((content) => content).length
      },
      { deep: true }
    )

    // step変更時はページ先頭にスクロールする
    watch(step, () => {
      scrollTop()
    })

    // ref群
    const formRef = ref<InstanceType<typeof ElForm>>()
    const boundary_application_form_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const identification_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const power_of_attorney_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const location_map_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const ground_plan_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const survey_drawing_of_acreage_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const cadastral_map_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const certificate_of_registered_matters_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const actual_place_photo_file_ref = ref<InstanceType<typeof JrFileInput>>()
    const fileRefs = [
      boundary_application_form_file_ref,
      identification_file_ref,
      power_of_attorney_file_ref,
      location_map_file_ref,
      ground_plan_file_ref,
      survey_drawing_of_acreage_file_ref,
      cadastral_map_file_ref,
      certificate_of_registered_matters_file_ref,
      actual_place_photo_file_ref,
    ]
    // 入力画面かどうか
    const isInputMode = computed(() => {
      return step.value === BoundarySteps.INPUT
    })
    // 確認画面かどうか
    const isConfirmMode = computed(() => {
      return step.value === BoundarySteps.CONFIRM
    })

    return {
      step,
      form,
      applicantNumber,
      formRef,
      rules: boundaryRule,
      fileRefs,
      isInputMode,
      isConfirmMode,
      validationError,
      RequestReasonTypes,
      FileTypes,
      allowExts: APPLICANT_UPLOAD_FILE_EXTS,
      mapCenterLat,
      mapCenterLng,
      mapZoom,
      changeMapCenter,
      boundary_application_form_file_ref,
      identification_file_ref,
      power_of_attorney_file_ref,
      location_map_file_ref,
      ground_plan_file_ref,
      survey_drawing_of_acreage_file_ref,
      cadastral_map_file_ref,
      certificate_of_registered_matters_file_ref,
      actual_place_photo_file_ref,
      sumpleSubmissionsUrl: process.env.VUE_APP_BOUNDARY_SUMPLE_SUBMISSIONS_URL,
    }
  },
})
